import { Link } from '@/components/Link';
import { LegalDoc, type LegalDocument, type LegalSection } from '../components/LegalDoc';
import { usdWhole } from '../lib/format';
import { SELLER_TERMS_EFFECTIVE, SELLER_TERMS_VERSION } from '../lib/sellerTerms';
import { SELLER_CHARGED_ANSWERS, SELLER_FAILURE_HOWTO, SELLER_FREE_ANSWERS } from '../lib/sellerChargeTable';
import { PAYOUT_MIN_USD } from '../lib/sellerPayouts';

const AnswerList = ({ answers }: { answers: readonly string[] }) => (
  <ul>
    {answers.map((answer) => (
      <li key={answer}>{answer}</li>
    ))}
  </ul>
);

const SECTIONS: LegalSection[] = [
  {
    heading: 'Selling on AnyAPI',
    body: (
      <>
        <p>
          The AnyAPI marketplace lets you sell data through AnyAPI by running your own HTTPS endpoint for an API in our catalog. These Seller Terms govern selling and are in addition to our <Link to="/terms">Terms of Service</Link> and <Link to="/acceptable-use">Acceptable Use Policy</Link>, which continue to apply to your AnyAPI account.
        </p>
        <p>
          You must be at least 18 years old to sell. If you accept these terms for a company, you confirm that you are authorized to bind it, and &ldquo;you&rdquo; means that company. You must accept these terms before you can register an endpoint. Selling does not make you an employee, agent, partner, or joint venturer of AnyAPI Labs, Inc. You are an independent contractor and are responsible for your own costs.
        </p>
      </>
    ),
  },
  {
    heading: 'What you are paid for',
    body: (
      <>
        <p>
          <strong>You are paid only for requests your endpoint serves</strong>, meaning the endpoint that delivers the result the buyer receives. An attempt that fails earns nothing, even if your endpoint was tried. If your endpoint rescues a request after another source failed, the request is yours and earns in full.
        </p>
        <p>
          What your endpoint answers decides whether the buyer is charged and you are paid. These answers are charged to the buyer, and you are paid for them:
        </p>
        <AnswerList answers={SELLER_CHARGED_ANSWERS} />
        <p>These answers are free to the buyer and unpaid, and the request goes to the next source:</p>
        <AnswerList answers={SELLER_FREE_ANSWERS} />
        <p>{SELLER_FAILURE_HOWTO}</p>
        <p>
          Requests funded by promotional or free credit are paid like any other request. Detecting fraud by buyers is our responsibility, not yours. A request paid through a crypto or agent payment method counts only once we have actually received that payment.
        </p>
      </>
    ),
  },
  {
    heading: 'Your price and our fee',
    body: (
      <>
        <p>
          You set your own price per request for each API you sell, up to that API&rsquo;s maximum seller price, which we set for each API and may change. We refuse a price above the maximum, and an endpoint whose price is above a lowered maximum gets no requests until you lower it.
        </p>
        <p>
          <strong>You are paid 80% of what the buyer paid for each request your endpoint serves; AnyAPI keeps 20%.</strong> Your share and our fee are shown on every row of your earnings.
        </p>
        <p>
          A price increase takes effect 3 days after you make it; a price decrease applies immediately.
        </p>
      </>
    ),
  },
  {
    heading: 'Traffic and routing',
    body: (
      <p>
        We do not guarantee any volume of traffic. Which endpoint or source serves a request is at our discretion, and we may consider price, reliability, latency, and other factors. We may limit, pause, or stop sending requests to your endpoint at any time and without notice, including when it fails, behaves unusually, or we suspect a breach of these terms.
      </p>
    ),
  },
  {
    heading: 'Running your endpoint',
    body: (
      <>
        <p>
          You are responsible for running your endpoint, including keeping it working as described, and for your own costs of operating it and collecting its data. The description and other information you give us about your endpoint must be true and accurate, and you will keep it up to date.
        </p>
        <p>
          You will fix problems you find with your endpoint, fix a problem we report to you within 14 days of our report, and respond promptly to our requests about your endpoint or these terms. We may test your endpoint at any time to confirm that it works and complies with these terms.
        </p>
      </>
    ),
  },
  {
    heading: 'Verifying answers',
    body: (
      <p>
        We may verify any answer your endpoint gives, at any time and at our own cost, against independent sources. An answer we find false is unpaid, and what it earned is taken back from your balance. Repeated false answers may lead us to pause your endpoint or end your selling. We decide these matters at our discretion.
      </p>
    ),
  },
  {
    heading: 'Refunds, chargebacks, and deductions',
    body: (
      <p>
        Refunds, chargebacks, and credits we give a buyer because a result your endpoint delivered was faulty may be deducted from your future earnings. A negative balance carries forward against earnings you make afterwards. We may withhold or reclaim earnings tied to fraud, to a breach of these terms, or to a buyer payment that fails or is reversed; requests funded by promotional or free credit are not affected by this. While we investigate suspected fraud or a suspected breach, we may hold the earnings tied to it. Until it is paid out, the balance shown in your dashboard may change because of these deductions.
      </p>
    ),
  },
  {
    heading: 'Payouts',
    body: (
      <>
        <p>
          Payouts are made to a verified PayPal account in your own name, in a country PayPal can send payouts to. Before your endpoint can serve traffic you must pass an identity check, which includes sanctions screening; we may decline or stop paying any seller where the law or a sanctions or export-control restriction requires it.
        </p>
        <p>
          Your available balance must reach {usdWhole(PAYOUT_MIN_USD)} before you can withdraw it; below that it stays available and is not forfeited. Your first payout unlocks 30 days after your seller account is approved. Each withdrawal is reviewed by us before it is sent, PayPal&rsquo;s fee for sending it is deducted from it, and it is paid in USD under PayPal&rsquo;s own terms.
        </p>
        <p>
          If your endpoint stops serving or you stop selling, earnings you have already accrued are still paid under these payout rules, subject to the deductions above.
        </p>
      </>
    ),
  },
  {
    heading: 'Taxes',
    body: (
      <p>
        You are responsible for all taxes on amounts you earn. Tax information you give us must be accurate and kept up to date. We may request a completed Form W-9, the applicable Form W-8, or another tax form, and we may pause payouts until it is received or apply withholding where the law requires it. Nothing here is tax advice.
      </p>
    ),
  },
  {
    heading: 'Your data sources',
    body: (
      <>
        <p>
          You represent and warrant that you have every right needed to collect the data your endpoint returns and to provide it to AnyAPI and its buyers, and that collecting and providing it complies with applicable law and with the terms of the sites and services you collect it from. You will not return data you have no right to provide, malicious content, or results you have fabricated.
        </p>
        <p>
          You grant AnyAPI a worldwide, non-exclusive, royalty-free license to call your endpoint, to store, process, transform, and deliver its results to buyers, who may use them under our Terms of Service, and to describe and promote the API your endpoint serves. We own the usage, performance, and quality data we record about your endpoint, such as request counts, latency, and error rates.
        </p>
        <p>
          You will tell us promptly if you become aware of any claim or complaint about the data your endpoint returns or how you collected it, and you will cooperate with us in responding to it. You will defend, indemnify, and hold harmless AnyAPI Labs, Inc. and its officers, employees, and agents from any claim, loss, or expense, including reasonable legal fees, arising from the data your endpoint returns, how you collected it, your violation of applicable law, including data protection law, or your breach of these terms.
        </p>
      </>
    ),
  },
  {
    heading: 'Buyer request data',
    body: (
      <>
        <p>
          The requests we send your endpoint may contain our buyers&rsquo; data. For that data you act as AnyAPI&rsquo;s sub-processor, on our instructions only. You will:
        </p>
        <ul>
          <li>use request data only to fulfil the request it came with;</li>
          <li>not sell it, share it, or use it to train or improve any model or product;</li>
          <li>not keep it longer than fulfilling the request requires;</li>
          <li>keep it confidential, and limit access to the people and systems that fulfil the request;</li>
          <li>protect it with appropriate technical and organisational security measures; and</li>
          <li>notify us at <a href="mailto:support@getanyapi.com">support@getanyapi.com</a> without undue delay after you become aware of any breach affecting it.</li>
        </ul>
      </>
    ),
  },
  {
    heading: 'Buyers and our marketplace',
    body: (
      <p>
        We do not give sellers our buyers&rsquo; identities or contact details. You will not use request data or any other information you obtain through AnyAPI to identify, contact, market to, or solicit a buyer, or to encourage a buyer to buy from you or anyone else outside AnyAPI. You will not add advertising or promotion of other products or services to the results your endpoint returns.
      </p>
    ),
  },
  {
    heading: 'Public listing',
    body: (
      <>
        <p>
          You agree that we may list you publicly on AnyAPI&rsquo;s sellers page and in related materials, by your seller display name or, if you choose to be anonymous, by the anonymous name AnyAPI assigns you.
        </p>
        <p>
          Your seller display name must not be misleading, impersonate anyone, or suggest an affiliation with or endorsement by a brand or trademark owner that you do not have.
        </p>
      </>
    ),
  },
  {
    heading: 'Review and removal',
    body: (
      <p>
        We have no obligation to monitor your endpoint or listing, but we may review either at any time. At our sole discretion and without prior notice, we may restrict, suspend, delist, or remove your endpoint or listing, or change how it is described, including where we believe it breaches these terms or the law, or a rights holder complains about it. Removing an endpoint does not by itself affect earnings already accrued, which remain payable as described under Payouts and Ending your selling.
      </p>
    ),
  },
  {
    heading: 'Changes to these terms',
    body: (
      <p>
        We may change these terms. We will tell you about a change by email or in your dashboard at least 7 days before it takes effect, including a change to the share of each payment you are paid, and the version and effective date at the top of this page will change with it. If you do not agree to a change, you may stop selling before it takes effect. Continuing to sell after a change takes effect means you accept it. We may ask you to accept a new version before you register or check another endpoint.
      </p>
    ),
  },
  {
    heading: 'Ending your selling',
    body: (
      <>
        <p>
          Either of us may end your selling at any time, on notice. We may suspend or end your selling immediately and without notice if you breach these terms, if we suspect fraud, if the law requires it, or to protect buyers or AnyAPI. Earnings already accrued remain payable as described above, except where we end it for fraud or breach of these terms.
        </p>
        <p>
          The sections on refunds, chargebacks, and deductions, taxes, your data sources, buyer request data, buyers and our marketplace, and liability survive the end of your selling.
        </p>
      </>
    ),
  },
  {
    heading: 'Liability and contact',
    body: (
      <>
        <p>
          To the maximum extent permitted by law, AnyAPI&rsquo;s total liability to you arising out of or relating to these Seller Terms or your selling is limited to $100. This cap applies in place of the liability cap in our <Link to="/terms">Terms of Service</Link>.
        </p>
        <p>
          The other limitations of liability, disclaimers, governing law, dispute resolution, and general provisions in our <Link to="/terms">Terms of Service</Link> apply to these Seller Terms and are incorporated by reference.
        </p>
        <p>
          Questions about selling go to <a href="mailto:support@getanyapi.com">support@getanyapi.com</a>.
        </p>
      </>
    ),
  },
];

/** Seller Terms, single-sourced alongside the other legal documents. */
const SELLER_TERMS_DOC: LegalDocument = {
  title: 'Seller Terms',
  updated: SELLER_TERMS_EFFECTIVE,
  intro: (
    <p>
      Version {SELLER_TERMS_VERSION}, effective {SELLER_TERMS_EFFECTIVE}. These Seller Terms are an agreement between you and AnyAPI Labs, Inc. (&ldquo;AnyAPI&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) and govern selling through the AnyAPI marketplace. By accepting them in your dashboard you agree to them. Capitalized terms not defined here have the meaning given in our <Link to="/terms">Terms of Service</Link>.
    </p>
  ),
  sections: SECTIONS,
};

export const SellerTermsPage = () => <LegalDoc doc={SELLER_TERMS_DOC} />;
